import { Clock, Calendar as CalendarIcon, Target } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { useTeacher } from '@/context/TeacherContext';

export default function TeacherCalendar() {
  const { currentClass, teacherTheme } = useTeacher();
  const isDark = teacherTheme === 'dark';

  return (
    <div className={cn(
      "w-full h-full overflow-y-auto p-8",
      isDark
        ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]"
        : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]"
    )}>
      <div className="max-w-7xl mx-auto w-full">
        <div className="mb-6">
          <h1 className={cn(
            "text-3xl font-bold",
            isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]"
          )}>
            Your Calendar
          </h1>
          <p className={cn(
            "mt-2 text-sm font-medium",
            isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]"
          )}>
            {currentClass ? `Schedule overview for ${currentClass.fullName || currentClass.name}` : 'Schedule overview for all classes'}
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-280px)]">
          <div className="lg:col-span-3 flex flex-col gap-4">
            <div className={cn("rounded-3xl p-5 shadow-sm flex-1", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#D4C4A8] to-[#CDB89A] border border-[#C9B08A]")}>
              <div className="flex items-center gap-2 mb-4">
                <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", isDark ? "bg-[hsl(var(--card-border))]/60" : "bg-white/30")}>
                  <Clock className={cn("w-3.5 h-3.5", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#1A1A1A]")} />
                </div>
                <h3 className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Week at a Glance</h3>
              </div>
              <div className="space-y-2">
                {(currentClass
                  ? [
                    { day: 'Mon', classes: 1, hours: '1.5h' },
                    { day: 'Wed', classes: 1, hours: '1.5h' },
                    { day: 'Fri', classes: 1, hours: '1.5h' }
                  ]
                  : [
                    { day: 'Mon', classes: 3, hours: '6h' },
                    { day: 'Tue', classes: 2, hours: '4h' },
                    { day: 'Wed', classes: 3, hours: '6h' },
                    { day: 'Thu', classes: 2, hours: '4h' },
                    { day: 'Fri', classes: 2, hours: '4h' }
                  ]
                ).map((day, i) => (
                  <div key={i} className={cn("flex items-center gap-2 rounded-xl p-2 border transition-all", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))] hover:bg-[hsl(var(--card-border))]/50" : "bg-white/50 border-white/30 hover:bg-white/70")}>
                    <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-xs shadow-sm flex-shrink-0", isDark ? "bg-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white text-[#1A1A1A]")}>
                      {day.day}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <div className={cn("w-1 h-1 rounded-full flex-shrink-0", isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#C9A961]")} />
                        <span className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{day.classes} Classes</span>
                      </div>
                      <p className={cn("text-xs font-light", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>{day.hours} teaching</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={cn("rounded-3xl p-4 shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#E8DCC8] to-[#DCC4A8] border border-[#C9B08A]")}>
              <div className="flex items-center gap-2 mb-3">
                <div className={cn("w-6 h-6 rounded-full flex items-center justify-center", isDark ? "bg-[hsl(var(--card-border))]/60" : "bg-white/30")}>
                  <Target className={cn("w-3 h-3", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#1A1A1A]")} />
                </div>
                <h3 className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{currentClass ? currentClass.name : 'This Week'}</h3>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className={cn("rounded-lg p-2 border text-center", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-white/60 border-white/40")}>
                  <span className={cn("text-base font-bold block", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{currentClass ? 3 : 12}</span>
                  <span className={cn("text-xs font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>Classes</span>
                </div>
                <div className={cn("rounded-lg p-2 border text-center", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-white/60 border-white/40")}>
                  <span className={cn("text-base font-bold block", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{currentClass ? currentClass.students : 150}</span>
                  <span className={cn("text-xs font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>Students</span>
                </div>
                <div className={cn("rounded-lg p-2 border text-center", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-white/60 border-white/40")}>
                  <span className={cn("text-base font-bold block", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{currentClass ? 4.5 : 30}</span>
                  <span className={cn("text-xs font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>Hours</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className={cn("rounded-3xl p-6 shadow-sm h-full flex items-center justify-center overflow-hidden", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border border-[#E0D4BF]")}>
              <div className="flex items-center justify-center w-full">
                <div className="scale-125 origin-center">
                  <Calendar className="w-full" />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 flex flex-col gap-4">
            <div className={cn("rounded-3xl p-5 shadow-sm flex-1", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#D4C4A8] to-[#CDB89A] border border-[#C9B08A]")}>
              <div className="flex items-center gap-2 mb-4">
                <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", isDark ? "bg-[hsl(var(--card-border))]/60" : "bg-white/30")}>
                  <CalendarIcon className={cn("w-3.5 h-3.5", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#1A1A1A]")} />
                </div>
                <h3 className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{currentClass ? `Events for ${currentClass.name}` : 'Upcoming Events'}</h3>
              </div>
              <div className="space-y-2">
                {[
                  { title: 'Parent-Teacher Meet', date: 'Dec 5, 2025', time: '10:00 AM', scope: 'all' },
                  { title: 'Science Fair', date: 'Dec 8, 2025', time: '2:00 PM', scope: 'all' },
                  { title: 'Staff Meeting', date: 'Dec 10, 2025', time: '4:00 PM', scope: 'all' },
                  { title: 'Physics Quiz Review', date: 'Dec 6, 2025', time: '11:00 AM', scope: 'class' }
                ].filter(e => currentClass ? e.scope === 'class' || e.title.includes('Parent') : e.scope === 'all')
                  .map((event, i) => (
                    <div key={i} className={cn("rounded-xl p-2.5 border", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-white/50 border-white/30")}>
                      <h4 className={cn("text-sm font-semibold mb-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{event.title}</h4>
                      <div className="flex items-center gap-1.5 text-xs">
                        <CalendarIcon className={cn("w-2.5 h-2.5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")} />
                        <span className={cn("font-light", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>{event.date}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs mt-0.5">
                        <Clock className={cn("w-2.5 h-2.5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")} />
                        <span className={cn("font-light", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#3D3D3D]")}>{event.time}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className={cn("rounded-3xl p-4 shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#E8DCC8] to-[#DCC4A8] border border-[#C9B08A]")}>
              <div className="flex items-center gap-2 mb-3">
                <div className={cn("w-6 h-6 rounded-full flex items-center justify-center", isDark ? "bg-[hsl(var(--card-border))]/60" : "bg-white/30")}>
                  <Target className={cn("w-3 h-3", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#1A1A1A]")} />
                </div>
                <h3 className={cn("text-sm font-semibold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Pending Tasks</h3>
              </div>
              <div className="space-y-2">
                {[
                  { task: 'Grade Physics Quiz', count: 25, scope: 'class' },
                  { task: 'Update Lesson Plans', count: 3, scope: 'all' },
                  { task: 'Review Assignments', count: 12, scope: 'class' }
                ].filter(t => currentClass ? t.scope === 'class' || t.task.includes('Grade') : true)
                  .map((item, i) => (
                    <div key={i} className={cn("flex items-center justify-between rounded-xl p-2 border", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-white/50 border-white/30")}>
                      <span className={cn("text-xs font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>{item.task}</span>
                      <span className={cn("text-xs font-bold px-1.5 py-0.5 rounded-full", isDark ? "bg-[hsl(var(--gold-primary))] text-[hsl(var(--foreground))]" : "bg-[#C9A961] text-white")}>
                        {currentClass ? Math.round(item.count / 4) : item.count}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
